import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ChristmasDeadlineCard } from "@/components/ChristmasDeadline";

const Section = ({ s }) => (
  <section className="mt-16 scroll-mt-28" id={s.key} data-testid={`christmas-section-${s.key}`}>
    <div className="flex items-end justify-between gap-4 border-b border-[#EDE4D3] pb-4">
      <div>
        <h2 className="font-serif text-2xl sm:text-3xl font-bold">{s.title}</h2>
        <p className="mt-1 text-slate-500">{s.subtitle}</p>
      </div>
      <span className="font-mono text-xs text-slate-400">{s.products.length} gifts</span>
    </div>
    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {s.products.map((p, i) => <ProductCard key={p.id} p={p} index={i % 8} />)}
    </div>
  </section>
);

export default function Christmas() {
  const [data, setData] = useState(null);
  const sections = data?.sections;
  useEffect(() => {
    api.get("/seasonal").then((r) => {
      setData(r.data);
      if (r.data.label) document.title = `${r.data.label} | Albion Pantry`;
    }).catch(() => setData({ sections: [] }));
    return () => { document.title = "Albion Pantry | Fine British Food"; };
  }, []);

  if (data && !data.key) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="seasonal-off">
      <h1 className="font-serif text-3xl sm:text-4xl font-bold">Seasonal gifts are coming soon</h1>
      <p className="mt-3 text-slate-500">Our next gift shop opens with the season. In the meantime, build your own hamper or browse the full shop.</p>
      <div className="mt-6 flex gap-3"><Link to="/hampers" className="rounded-full bg-[#1E2A44] text-white px-6 py-3 font-semibold" data-testid="seasonal-off-hampers">Build a hamper</Link><Link to="/" className="rounded-full border border-[#1E2A44] px-6 py-3 font-semibold" data-testid="seasonal-off-shop">Shop all</Link></div>
    </div>
  );
  const bg = { christmas: "bg-[#8C2220]", easter: "bg-[#3F6B4E]", mothers_day: "bg-[#7A3E5A]" }[data?.key] || "bg-[#1E2A44]";
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="seasonal-page">
      <div className={`rounded-3xl ${bg} text-white p-8 sm:p-12 relative overflow-hidden`}>
        <Gift className="absolute -right-6 -bottom-6 w-48 h-48 text-white/10" />
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#F3D9A4]" data-testid="seasonal-label">{data?.label}</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold max-w-2xl" data-testid="seasonal-title">{data?.title}</h1>
        <p className="mt-4 max-w-xl text-white/80">{data?.intro}</p>
        {data?.key === "christmas" && <ChristmasDeadlineCard />}
        <Link to="/hampers" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F3D9A4] text-[#1E293B] px-6 py-3 font-semibold hover:bg-white transition-colors" data-testid="christmas-build-hamper">
          <Gift className="w-4 h-4" /> Build your own hamper
        </Link>
        {sections?.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="christmas-jump-links">
            {sections.map((s) => (
              <a key={s.key} href={`#${s.key}`} className="rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm transition-colors" data-testid={`christmas-jump-${s.key}`}>{s.title}</a>
            ))}
          </div>
        )}
      </div>
      {!sections && <p className="mt-12 text-slate-500">Loading gifts…</p>}
      {sections?.length === 0 && <p className="mt-12 text-slate-500" data-testid="seasonal-empty">New gifts for this season are coming soon.</p>}
      {sections?.map((s) => <Section key={s.key} s={s} />)}
    </div>
  );
}
